import { normalizeAppRole, isQaTesterRole } from "../utils/roleUtils";

export const BUG_ACTIVE_STATUSES = ["Open", "In Progress", "Testing", "Blocked"];
export const BUG_RESOLUTION_STATUSES = [
  "Fixed",
  "Not Reproducible",
  "Invalid",
  "Won't Fix",
  "Duplicate",
  "By Design",
];
export const BUG_QA_VERIFY_STATUSES = ["Closed", "Reopened"];

const STEWARD_ROLES = new Set([
  "admin",
  "superadmin",
  "manager",
  "lead",
  "product",
  "product_manager",
  "analyst",
]);

export function canUseFullBugStatusMenu(role) {
  return STEWARD_ROLES.has(normalizeAppRole(role));
}

const DEVELOPER_BUG_ROLES = new Set(["developer", "designer", "devops", "member"]);

export function isDeveloperBugRole(role) {
  const r = normalizeAppRole(role);
  if (DEVELOPER_BUG_ROLES.has(r)) return true;
  return !canUseFullBugStatusMenu(r) && !isQaTesterRole(r);
}

/** QA verify transitions — Closed / Reopened only (not resolution statuses). */
export function getBugVerifyStatuses(role) {
  const r = normalizeAppRole(role);
  if (isQaTesterRole(r) || canUseFullBugStatusMenu(r)) {
    return BUG_QA_VERIFY_STATUSES;
  }
  return [];
}

/** Status groups for bug detail dropdown — developers vs QA vs stewards. */
export function getBugStatusOptionGroups(role) {
  const r = normalizeAppRole(role);
  const verify = getBugVerifyStatuses(r);

  if (canUseFullBugStatusMenu(r)) {
    return {
      active: BUG_ACTIVE_STATUSES,
      closed: BUG_RESOLUTION_STATUSES,
      verify,
      showClosedResolution: true,
    };
  }
  if (isQaTesterRole(r)) {
    return {
      active: BUG_ACTIVE_STATUSES,
      closed: [],
      verify,
      showClosedResolution: false,
    };
  }
  return {
    active: BUG_ACTIVE_STATUSES,
    closed: BUG_RESOLUTION_STATUSES,
    verify: [],
    showClosedResolution: true,
  };
}

export function isBugClosedStatus(status) {
  const s = String(status || "");
  return (
    BUG_RESOLUTION_STATUSES.includes(s) ||
    s === "Closed" ||
    s === "Cannot Reproduce"
  );
}

/** True when QA has verified and closed the bug — list/UI “done” styling. */
export function isBugQaVerifiedClosed(status) {
  return String(status || "") === "Closed";
}

/** Bugs tied to a project's Active sprint. */
export function bugsForActiveSprint(projectId, issues, sprints) {
  const active = (sprints || []).find(
    (s) => String(s.project_id) === String(projectId) && s.status === "Active",
  );
  if (!active) return { activeSprint: null, bugs: [] };
  const sid = String(active.id);
  const bugs = (issues || []).filter(
    (i) => String(i.projectid) === String(projectId) && String(i.sprint_id) === sid,
  );
  return { activeSprint: active, bugs };
}

export function activeSprintBugCounts(projectId, issues, sprints) {
  const { activeSprint, bugs } = bugsForActiveSprint(projectId, issues, sprints);
  return {
    activeSprint,
    open: bugs.filter((b) => !isBugClosedStatus(b.status)).length,
    fixed: bugs.filter((b) => isBugClosedStatus(b.status)).length,
  };
}

export function allProjectsActiveSprintBugCounts(issues, sprints) {
  const activeIds = new Set(
    (sprints || []).filter((s) => s.status === "Active").map((s) => String(s.id)),
  );
  const bugs = (issues || []).filter(
    (i) => i.sprint_id != null && activeIds.has(String(i.sprint_id)),
  );
  return {
    open: bugs.filter((b) => !isBugClosedStatus(b.status)).length,
    fixed: bugs.filter((b) => isBugClosedStatus(b.status)).length,
  };
}
